import { CircleProvider } from '@/circle/contexts/CIrcleContext'
import { RoleEditableField } from '@/circle/components/RoleEditableField'
import { RoleWriteScopeProvider } from '@/role/contexts/RoleWriteScopeContext'
import { Box, Text } from '@chakra-ui/react'
import { useUpdateMeetingStepMutation } from '@gql'
import { MeetingStepIndicatorsFragment } from '@rolebase/shared/model/meeting_step'
import React, { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { MeetingContext } from '../contexts/MeetingContext'
import useMeetingStepRoles from '../hooks/useMeetingStepRoles'
import MeetingStepRoleFilter from './MeetingStepRoleFilter'

interface Props {
  step: MeetingStepIndicatorsFragment
}

// The Indicators step: one editable block per role of the circle and of its
// direct children (REU-04).
//
// 🔑 Editing writes straight into the role, not into the meeting: an indicator
// is a current state, "comme le nombre de likes sur FB", not a per-meeting
// tally (REU04-D2). The meeting therefore keeps no memory of the value of the
// day — assumed, and to be covered later by the end-of-meeting email (D16).
//
// Each block is a plain RoleEditableField, the very component the role panel
// uses. That inherits its rights, its base-role alert and the single write path
// of GOUV-15 for free; the RoleWriteScope around them adds the one thing a
// meeting changes — anyone in the room may type, for any role shown here.
export default function MeetingStepContentIndicators({ step }: Props) {
  const { t } = useTranslation()
  const { meeting, editable, isEnded } = useContext(MeetingContext)!
  const [updateMeetingStep] = useUpdateMeetingStepMutation()

  const filterRolesIds = step.data.filterRolesIds
  const { all: allRoles, filtered: roles } = useMeetingStepRoles(
    'indicators',
    filterRolesIds
  )

  // 🔴 Must say exactly what the server says. `editable` stays true on an ended
  // meeting reopened in forced edit, while the meeting door refuses as soon as
  // `ended` is set: without this, a non-holder would type into a field the
  // server then rejects in silence — the 08/08/2026 failure, again.
  const canWriteRoles = editable && !isEnded

  const handleFilterChange = useCallback(
    (rolesIds: string[]) =>
      updateMeetingStep({
        variables: {
          id: step.id,
          values: { data: { ...step.data, filterRolesIds: rolesIds } },
        },
      }),
    [step.id, step.data, updateMeetingStep]
  )

  if (!meeting) return null

  return (
    <Box>
      <MeetingStepRoleFilter
        roles={allRoles.map((r) => r.role)}
        selectedIds={filterRolesIds ?? []}
        editable={editable}
        onChange={handleFilterChange}
      />

      {roles.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={6}>
          {filterRolesIds?.length
            ? t('MeetingStepContentIndicators.emptyFiltered')
            : t('MeetingStepContentIndicators.empty')}
        </Text>
      ) : (
        <RoleWriteScopeProvider meetingId={meeting.id} canWrite={canWriteRoles}>
          {roles.map(({ circle, role }) => (
            <CircleProvider key={role.id} circleId={circle.id}>
              <RoleEditableField
                label={role.name}
                role={role}
                field="indicators"
                initList="bullet"
                mb={6}
              />
            </CircleProvider>
          ))}
        </RoleWriteScopeProvider>
      )}
    </Box>
  )
}
